import { Injectable, effect, inject, signal } from '@angular/core';
import { LoggerService } from './logger.service';
import { ToastService } from './toast.service';
import { TranslationService } from './translation.service';
import { UserStorageService } from './user-storage.service';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type PlatformType = 'android' | 'ios' | 'desktop' | 'unknown';

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private toast = inject(ToastService);

  // New key — only written when user explicitly defers. Old key 'pwa_install_prompt_last_shown'
  // was written whenever the old toast was shown, blocking all new users with stale timestamps.
  private static readonly DEFER_KEY = 'pwa_install_modal_deferred_until';
  // Show again after 3 days if the user defers
  private static readonly DEFER_DEBOUNCE_MS = 3 * 24 * 60 * 60 * 1000;
  // Delay before auto-showing the modal on page load (ms)
  private static readonly SHOW_DELAY_MS = 8000;

  private deferredPrompt = signal<BeforeInstallPromptEvent | null>(null);
  private isInstalled = signal(false);
  private userStorage = inject(UserStorageService);
  private platform = signal<PlatformType>('unknown');
  private hasShownPrompt = signal(false);
  private logger = inject(LoggerService).getLogger('app');
  private translate = inject(TranslationService);

  // Drives modal visibility — consumed by app.html via pwaInstallService.showModal()
  private readonly _showModal = signal(false);
  readonly showModal = this._showModal.asReadonly();

  readonly canInstall = this.deferredPrompt.asReadonly();
  readonly installed = this.isInstalled.asReadonly();
  readonly detectedPlatform = this.platform.asReadonly();

  constructor() {
    this.detectPlatform();
    this.setupEventListeners();
    this.checkIfInstalled();

    this.logger.info(
      'PWA: init — platform:',
      this.platform(),
      '| installed:',
      this.isInstalled(),
      '| canShowPrompt:',
      this.canShowPrompt(),
    );

    // Android/Desktop: show modal shortly after beforeinstallprompt fires
    effect(() => {
      if (
        this.deferredPrompt() &&
        !this.hasShownPrompt() &&
        !this.isInstalled() &&
        this.canShowPrompt()
      ) {
        setTimeout(() => {
          if (!this.hasShownPrompt() && !this.isInstalled() && this.canShowPrompt()) {
            this.showInstallPrompt();
          }
        }, PwaInstallService.SHOW_DELAY_MS);
      }
    });

    // iOS: beforeinstallprompt never fires, so we schedule unconditionally
    if (this.platform() === 'ios') {
      this.logger.info(
        'PWA: iOS detected — scheduling install modal in',
        PwaInstallService.SHOW_DELAY_MS,
        'ms',
      );
      setTimeout(() => {
        this.logger.info(
          'PWA: iOS timeout fired — hasShown:',
          this.hasShownPrompt(),
          '| installed:',
          this.isInstalled(),
          '| canShow:',
          this.canShowPrompt(),
        );
        if (!this.hasShownPrompt() && !this.isInstalled() && this.canShowPrompt()) {
          this.showInstallPrompt();
        }
      }, PwaInstallService.SHOW_DELAY_MS);
    }
  }

  /**
   * Check if enough time has passed since the user last explicitly deferred
   */
  private canShowPrompt(): boolean {
    const deferredUntil = this.userStorage.getGlobalItem(PwaInstallService.DEFER_KEY);
    if (!deferredUntil) {
      return true;
    }
    // Local dismiss-cooldown check -- write and read both happen on this same device, so
    // drift correction doesn't apply.
    // eslint-disable-next-line no-restricted-syntax
    const canShow = Date.now() >= parseInt(deferredUntil, 10);
    this.logger.info(
      'PWA: canShowPrompt =',
      canShow,
      '(deferred until',
      new Date(parseInt(deferredUntil, 10)).toISOString() + ')',
    );
    return canShow;
  }

  private recordDeferred(): void {
    // eslint-disable-next-line no-restricted-syntax -- local dismiss-cooldown, see canShowPrompt() above
    const deferUntil = Date.now() + PwaInstallService.DEFER_DEBOUNCE_MS;
    this.userStorage.setGlobalItem(PwaInstallService.DEFER_KEY, deferUntil.toString());
  }

  private detectPlatform(): void {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isDesktop = !isIOS && !isAndroid;

    if (isIOS) {
      this.platform.set('ios');
    } else if (isAndroid) {
      this.platform.set('android');
    } else if (isDesktop) {
      this.platform.set('desktop');
    }
  }

  private setupEventListeners(): void {
    // Listen for the beforeinstallprompt event (Chrome/Edge/Android)
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferredPrompt.set(e as BeforeInstallPromptEvent);
      this.logger.info('PWA: beforeinstallprompt fired');
    });

    // Listen for successful installation
    window.addEventListener('appinstalled', () => {
      this.isInstalled.set(true);
      this._showModal.set(false);
      this.deferredPrompt.set(null);
      this.toast.success(this.translate.translate('pwa.install.success'));
      this.logger.info('PWA: App installed via appinstalled event');
    });
  }

  private checkIfInstalled(): void {
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true
    ) {
      this.isInstalled.set(true);
    }
  }

  /**
   * Open the install modal
   */
  showInstallPrompt(): void {
    if (this.isInstalled()) return;

    this.hasShownPrompt.set(true);
    this._showModal.set(true);
    this.logger.info('PWA: Showing install modal for platform:', this.platform());
  }

  /**
   * User chose "Remind me later" — snooze for DEFER_DEBOUNCE_MS (3 days)
   */
  defer(): void {
    this._showModal.set(false);
    this.recordDeferred();
    this.logger.info('PWA: Install modal deferred for 3 days');
  }

  /**
   * Trigger the native install prompt (Android/Desktop only).
   * On iOS this is a no-op (user follows the manual steps in the modal).
   */
  async promptInstall(): Promise<boolean> {
    const prompt = this.deferredPrompt();
    if (!prompt) {
      return false;
    }

    try {
      this.logger.info('PWA: Triggering native install prompt');
      await prompt.prompt();
      const choiceResult = await prompt.userChoice;
      this.deferredPrompt.set(null);
      if (choiceResult.outcome === 'accepted') {
        this._showModal.set(false);
      }
      return choiceResult.outcome === 'accepted';
    } catch (error) {
      this.logger.error('PWA: Error prompting install', error);
      return false;
    }
  }

  /**
   * Reset the "has shown prompt" flag (for testing)
   */
  resetPromptFlag(): void {
    this.hasShownPrompt.set(false);
  }
}
